import { gql, useMutation } from '@apollo/client'

import { NON_EXISTENT_PARENT_COMMENT_ID } from '../../../constants'
import {
  AddCommentMutation,
  AddCommentMutationVariables,
} from '../components/__generated__/mutations.graphql'
import { AddComment } from '../components/mutations'
import { UseAddComment_DocumentFragment } from './__generated__/use-add-comment.graphql'

export type UseAddCommentProps = {
  document: UseAddComment_DocumentFragment
  commentText: string
  commentId: number
}

export type UseAddCommentReturn = {
  loading: boolean
  addComment: () => Promise<void>
}

export function useAddComment({
  document,
  commentText,
  commentId,
}: UseAddCommentProps): UseAddCommentReturn {
  const { docId, artifact } = document
  const [mutate, { loading }] = useMutation<AddCommentMutation, AddCommentMutationVariables>(
    AddComment,
    {
      update(cache, { data: mutate }) {
        cache.modify({
          id: cache.identify(document),
          fields: {
            comments(commentsFromCache) {
              const comment = mutate?.addComment?.comment
              const commentNodeRef = cache.writeFragment({
                data: {
                  ...mutate?.addComment?.comment,
                  __typename: 'DocumentComment',
                  id: comment?.docId,
                  profile: comment?.publisher,
                  viewerUpVoted: comment?.viewerLiked,
                  viewerDownVoted: !comment?.viewerLiked,
                  upVotes: comment?.votesAmount,
                  parentCommentId: NON_EXISTENT_PARENT_COMMENT_ID,
                  replies: [],
                },
                fragment: useAddComment.fragments.comment,
              })
              return {
                ...commentsFromCache,
                edges: [
                  ...commentsFromCache.edges,
                  {
                    __typename: 'DocumentCommentsEdge',
                    cursor: '',
                    node: commentNodeRef,
                  },
                ],
              }
            },
            numComments() {
              return mutate?.addComment?.document?.numComments || 0
            },
          },
        })
      },
    }
  )

  return {
    async addComment() {
      const input = { docId, artifact, text: commentText, commentId }
      await mutate({ variables: { input } })
    },
    loading,
  }
}

useAddComment.fragments = {
  document: gql`
    fragment UseAddComment_document on Document {
      docId
      artifact
    }
  `,
  comment: gql`
    fragment UseAddComment_comment on DocumentComment {
      id
      text
      date
      profile {
        url
        fullname
        thumborImage {
          current: thumbUrl(size: "50x50")
        }
        hasProBadge
        isViewer
      }
      parentCommentId
      viewerUpVoted
      upVotes
      viewerDownVoted
      replies {
        id
      }
    }
  `,
}
